import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  ShieldCheck,
  Globe,
  Copy,
  Check,
  Trash2,
  CheckCircle2,
  Loader2,
  Sparkles
} from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import CustomDropdown from "../components/CustomDropdown";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const WhatsAppConfig = ({ activeAiAgentId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [whatsappConfig, setWhatsappConfig] = useState({
    access_token: "",
    phone_number_id: "",
    waba_id: "",
    verify_token: "",
    communication_mode: "bidirectional",
    is_active: false,
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [dbConfig, setDbConfig] = useState(null);

  // ─── Embedded Signup State ─────────────────────────────────────────────────
  const [embeddedConfig, setEmbeddedConfig] = useState(null);
  const [signupLoading, setSignupLoading] = useState(false);
  const pendingSignupData = useRef(null);

  useEffect(() => {
    ApiClient.request("/whatsapp/embedded-signup/app-config", { aiAgentId: activeAiAgentId })
      .then((res) => { if (res.success) setEmbeddedConfig(res); })
      .catch(() => {});
  }, [activeAiAgentId]);

  useEffect(() => {
    if (!embeddedConfig || document.getElementById("fb-jssdk")) return;
    window.fbAsyncInit = function () {
      window.FB.init({ appId: embeddedConfig.app_id, autoLogAppEvents: true, xfbml: true, version: "v25.0" });
    };
    const script = document.createElement("script");
    script.id = "fb-jssdk";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, [embeddedConfig]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) return;

      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type === "WA_EMBEDDED_SIGNUP") {
          if (data.event !== "FINISH") return;
          const sessionData = data.data || data;
          const waba_id = sessionData?.waba_id;
          const phone_number_id = sessionData?.phone_number_id;
          if (waba_id && phone_number_id) {
            pendingSignupData.current = {
              ...(pendingSignupData.current || {}),
              waba_id,
              phone_number_id,
            };
          }
        }
      } catch (_) {}
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const launchWhatsAppSignup = () => {
    if (!embeddedConfig || !window.FB) {
      showToast("Facebook SDK not ready yet. Please wait a moment and try again.", "error");
      return;
    }
    pendingSignupData.current = null;
    setSignupLoading(true);

    window.FB.login(
      (response) => {
        if (response.authResponse) {
          const code = response.authResponse.code;
          pendingSignupData.current = {
            ...(pendingSignupData.current || {}),
            code,
          };

          (async () => {
            let signup = null;
            for (let i = 0; i < 10; i++) {
              await new Promise((r) => setTimeout(r, 300));
              const current = pendingSignupData.current;
              if (current?.code && current?.waba_id && current?.phone_number_id) {
                signup = current;
                break;
              }
            }

            if (!signup?.code || !signup?.waba_id || !signup?.phone_number_id) {
              showToast("Signup completed but WABA/Phone data was missing. Please retry.", "error");
              setSignupLoading(false);
              return;
            }

            try {
              const res = await ApiClient.request("/whatsapp/embedded-signup", {
                aiAgentId: activeAiAgentId,
                method: "POST",
                body: JSON.stringify(signup),
              });
              if (res.success) {
                showToast("WhatsApp connected successfully! 🎉", "success");
                await loadConfig();
                setWebhookUrl(res.webhook_url || "");
              } else {
                throw new Error(res.detail || "Unknown error");
              }
            } catch (err) {
              showToast("Failed to complete WhatsApp setup: " + (err?.message || "Try again."), "error");
            } finally {
              setSignupLoading(false);
            }
          })();
        } else {
          showToast("WhatsApp signup was cancelled.", "info");
          setSignupLoading(false);
        }
      },
      {
        config_id: embeddedConfig.config_id,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          sessionInfoVersion: 2,
        },
      }
    );
  };

  const loadConfig = useCallback(async () => {
    try {
      const response = await ApiClient.request("/whatsapp/config", { aiAgentId: activeAiAgentId });
      if (response.success && response.config) {
        setWhatsappConfig({
          access_token: response.config.access_token ? UNCHANGED_KEY : "",
          phone_number_id: response.config.phone_number_id || "",
          waba_id: response.config.waba_id || "",
          verify_token: response.config.verify_token ? UNCHANGED_KEY : "",
          communication_mode: response.config.communication_mode || "bidirectional",
          is_active: response.config.is_active || false,
        });
        setWebhookUrl(response.config.webhook_url || "");
        setDbConfig(response.config);
      } else {
        setDbConfig(null);
      }
    } catch (error) {
      console.log("WhatsApp config not found");
      setDbConfig(null);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async () => {
    if (!whatsappConfig.access_token.trim()) {
      showToast("Please enter your Access Token", "error");
      return;
    }
    if (!whatsappConfig.phone_number_id.trim()) {
      showToast("Please enter your Phone Number ID", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await ApiClient.request("/whatsapp/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify(whatsappConfig),
      });

      if (response.success) {
        setWebhookUrl(response.webhook_url);
        showToast("WhatsApp configuration saved successfully!", "success");
        await loadConfig();
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/ai-agents/integrations/whatsapp", {
        method: "DELETE",
      });
      if (response.success) {
        setWhatsappConfig({
          access_token: "",
          phone_number_id: "",
          waba_id: "",
          verify_token: "",
          communication_mode: "bidirectional",
          is_active: false,
        });
        setWebhookUrl("");
        setDbConfig(null);
        showToast("WhatsApp integration deleted successfully!", "success");
      }
    } catch (error) {
      // Handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const getDisplayWebhookUrl = () => {
    if (!webhookUrl) return "";
    let path = webhookUrl;
    if (webhookUrl.startsWith("http://") || webhookUrl.startsWith("https://")) {
      try {
        const urlObj = new URL(webhookUrl);
        path = urlObj.pathname + urlObj.search;
      } catch (e) {}
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    let cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    let cleanPath = path.startsWith("/") ? path : "/" + path;
    if (cleanBase.endsWith("/api") && cleanPath.startsWith("/api")) {
      cleanBase = cleanBase.slice(0, -4);
    }
    return cleanBase + cleanPath;
  };

  const displayUrl = getDisplayWebhookUrl();

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      showToast("Webhook URL copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showToast("Failed to copy URL", "error");
    }
  };

  const toggleActive = async () => {
    if (!whatsappConfig.is_active) {
      if (!whatsappConfig.access_token?.trim() || !whatsappConfig.phone_number_id?.trim()) {
        showToast("Please complete and save your WhatsApp config first", "error");
        return;
      }

      const response = await ApiClient.request("/whatsapp/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({
          ...whatsappConfig,
          is_active: true,
        }),
      });

      if (response.success) {
        setWhatsappConfig((prev) => ({ ...prev, is_active: true }));
        showToast("WhatsApp integration activated!", "success");
      }
    } else {
      const response = await ApiClient.request("/whatsapp/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({
          ...whatsappConfig,
          is_active: false,
        }),
      });

      if (response.success) {
        setWhatsappConfig((prev) => ({ ...prev, is_active: false }));
        showToast("WhatsApp integration deactivated", "info");
      }
    }
  };

  const isConnected = !!(dbConfig && dbConfig.access_token);

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-4 sm:space-y-6">
        
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 sm:pb-5 border-b border-zinc-100 gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/integrations")}
              className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
              title="Back to Integrations"
            >
              <ArrowLeft size={16} />
            </button>
            <div className="flex items-center gap-2.5">
              <svg className="w-5 h-5 text-[#25D366] shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.119 1.533 5.85L.057 23.428a.5.5 0 00.609.61l5.697-1.498A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.94 0-3.763-.522-5.33-1.432l-.385-.226-3.98 1.048 1.054-3.88-.236-.394A9.956 9.956 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                </svg>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">WhatsApp Cloud Integration</h1>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Deploy autonomous AI assistance on WhatsApp Business channels.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5 self-start sm:self-auto pl-9 sm:pl-0">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase border ${
              whatsappConfig.is_active
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-zinc-50 text-zinc-500 border-zinc-200"
            }`}>
              {whatsappConfig.is_active ? "Active" : "Disabled"}
            </span>
            <button
              type="button"
              onClick={toggleActive}
              className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 ${
                whatsappConfig.is_active ? "bg-zinc-900" : "bg-zinc-200"
              }`}
              title={whatsappConfig.is_active ? "Deactivate integration" : "Activate integration"}
            >
              <div
                className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                  whatsappConfig.is_active ? "left-4" : "left-0.5"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Embedded One-Click Connect Box */}
        {embeddedConfig && (
          <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <Sparkles className="text-emerald-600 shrink-0" size={18} />
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Quick Connect with Meta</h3>
                    <span className="px-1.5 py-0.2 text-[10px] font-medium bg-zinc-100 text-zinc-600 rounded">
                      Recommended
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Launch official Meta embedded signup to automatically link your WhatsApp Business Account.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={launchWhatsAppSignup}
                disabled={signupLoading || isConnected}
                className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors flex-shrink-0 self-start sm:self-auto ${
                  isConnected
                    ? "bg-zinc-100 text-zinc-700 border border-zinc-200 cursor-default"
                    : signupLoading
                    ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
                    : "bg-zinc-900 text-white hover:bg-zinc-800"
                }`}
              >
                {signupLoading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : isConnected ? (
                  <CheckCircle2 size={14} className="text-emerald-600" />
                ) : null}
                <span>
                  {signupLoading
                    ? "Connecting..."
                    : isConnected
                    ? "WhatsApp Connected"
                    : "Connect with WhatsApp"}
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Manual Credentials Card */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-5">
          <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
            <ShieldCheck className="text-zinc-800 shrink-0" size={18} />
            <div>
              <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Cloud API Credentials</h2>
              <p className="text-[11px] text-zinc-500">Manual setup using credentials from your Meta for Developers dashboard</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                Permanent Access Token
              </label>
              <input
                type="password"
                value={whatsappConfig.access_token === UNCHANGED_KEY ? MASK_DISPLAY : whatsappConfig.access_token}
                onChange={(e) =>
                  setWhatsappConfig({
                    ...whatsappConfig,
                    access_token: e.target.value,
                  })
                }
                onFocus={() => {
                  if (whatsappConfig.access_token === UNCHANGED_KEY) {
                    setWhatsappConfig({ ...whatsappConfig, access_token: "" });
                  }
                }}
                placeholder={whatsappConfig.access_token === UNCHANGED_KEY ? MASK_DISPLAY : "EAAG..."}
                className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                  Phone Number ID
                </label>
                <input
                  type="text"
                  value={whatsappConfig.phone_number_id}
                  onChange={(e) =>
                    setWhatsappConfig({
                      ...whatsappConfig,
                      phone_number_id: e.target.value,
                    })
                  }
                  placeholder="e.g. 10458291823901"
                  className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                  WhatsApp Business Account ID (WABA)
                </label>
                <input
                  type="text"
                  value={whatsappConfig.waba_id}
                  onChange={(e) =>
                    setWhatsappConfig({
                      ...whatsappConfig,
                      waba_id: e.target.value,
                    })
                  }
                  placeholder="e.g. 10928472910382"
                  className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                  Verify Token
                </label>
                <input
                  type="password"
                  value={whatsappConfig.verify_token === UNCHANGED_KEY ? MASK_DISPLAY : whatsappConfig.verify_token}
                  onChange={(e) =>
                    setWhatsappConfig({
                      ...whatsappConfig,
                      verify_token: e.target.value,
                    })
                  }
                  onFocus={() => {
                    if (whatsappConfig.verify_token === UNCHANGED_KEY) {
                      setWhatsappConfig({ ...whatsappConfig, verify_token: "" });
                    }
                  }}
                  placeholder={whatsappConfig.verify_token === UNCHANGED_KEY ? MASK_DISPLAY : "Your webhook verification token"}
                  className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                  Communication Mode
                </label>
                <CustomDropdown
                  value={whatsappConfig.communication_mode || "bidirectional"}
                  onChange={(val) =>
                    setWhatsappConfig({
                      ...whatsappConfig,
                      communication_mode: val,
                    })
                  }
                  options={[
                    { value: "bidirectional", label: "Bidirectional (Send & Receive)" },
                    { value: "unidirectional", label: "Unidirectional (Send Only)" },
                  ]}
                  placeholder="Select mode"
                />
              </div>
            </div>
          </div>

          <div className="pt-3 border-t border-zinc-100 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2.5">
            {whatsappConfig.phone_number_id ? (
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={saving}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-40"
              >
                <Trash2 size={14} />
                <span>Disconnect WhatsApp</span>
              </button>
            ) : <div />}

            <button
              type="button"
              onClick={saveConfig}
              disabled={saving || !whatsappConfig.access_token?.trim() || !whatsappConfig.phone_number_id?.trim()}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-40 text-xs font-medium transition-colors"
            >
              {saving ? (
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Save size={14} />
              )}
              <span>{saving ? "Saving..." : "Save Settings"}</span>
            </button>
          </div>
        </div>

        {/* Webhook Endpoint Card */}
        {displayUrl && (
          <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-3">
            <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
              <Globe className="text-zinc-800 shrink-0" size={18} />
              <div>
                <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">WhatsApp Webhook Callback URL</h3>
                <p className="text-[11px] text-zinc-500">Configure this in Meta for Developers under WhatsApp → Configuration → Webhook</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-1.5 sm:p-2 bg-zinc-50 rounded-lg border border-zinc-200">
              <div className="font-mono text-[11px] sm:text-xs text-zinc-700 px-2 py-1 flex-1 break-all select-all min-w-0">
                {displayUrl}
              </div>
              <button
                type="button"
                onClick={copyWebhookUrl}
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-shrink-0 ${
                  copied
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-white text-zinc-800 border border-zinc-200 hover:bg-zinc-100"
                }`}
                title={copied ? "Copied!" : "Copy Webhook URL"}
              >
                {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                <span>{copied ? "Copied" : "Copy URL"}</span>
              </button>
            </div>

            <p className="text-[11px] text-zinc-400">
              Subscribe to the <code className="text-zinc-600 bg-zinc-100 px-1 py-0.5 rounded text-[10px]">messages</code> webhook field in your Meta app dashboard.
            </p>
          </div>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Disconnect WhatsApp?"
        message="Are you sure you want to delete this integration config? This will remove all credentials and disable the WhatsApp webhook."
        confirmText="Disconnect"
        isLoading={saving}
      />
    </div>
  );
};

export default WhatsAppConfig;
